import React from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import ClickableCardLink from '../common/ClickableCardLink';
import { clickableCardShellSx } from '../common/clickableCardSx';
import FittedCoursePreviewBoards from '../courses/FittedCoursePreviewBoards';
import CourseStartedChip from '../courses/CourseStartedChip';
import { formatProgressLabel } from '../courses/courseHelpers';
import { courseCardPath } from '../courses/coursesUrl';
import type { CourseListItemDto } from '../courses/types';
import {
  dashboardCardSecondarySx,
  dashboardCardTitleSx,
  dashboardInteractiveCardSx,
} from './dashboardMuiStyles';

type DashboardCourseCardProps = {
  course: CourseListItemDto;
  /** When true, resume at last lesson if available (dashboard default). */
  linkToLastLesson?: boolean;
  /** When set, renders a selectable button instead of a navigation link. */
  onSelect?: () => void;
  selected?: boolean;
};

const courseCardShellSx = {
  ...dashboardInteractiveCardSx,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
} as const;

const selectedCourseCardShellSx: SxProps<Theme> = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: 2,
};

export const DashboardCourseCardContent = ({
  course,
}: {
  course: CourseListItemDto;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 1.25, xxl: 1.5, xxxl: 1.75 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap title={course.title} sx={dashboardCardTitleSx}>
          {course.title}
        </Typography>
        <Typography sx={dashboardCardSecondarySx} display="block">
          {formatProgressLabel(course.completedLessonCount, course.lessonCount)}
        </Typography>
      </Box>
      <CourseStartedChip course={course} />
    </Box>
    <FittedCoursePreviewBoards
      previews={course.previewThumbnails}
      trainSide={course.trainSide}
    />
  </Box>
);

export const DashboardCourseCard = ({
  course,
  linkToLastLesson = true,
  onSelect,
  selected = false,
}: DashboardCourseCardProps) => {
  if (onSelect) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onSelect}
        aria-label={course.title}
        aria-pressed={selected}
        sx={
          [
            clickableCardShellSx,
            courseCardShellSx,
            {
              width: '100%',
              bgcolor: 'background.paper',
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
            },
            selected ? selectedCourseCardShellSx : null,
          ] as SxProps<Theme>
        }
      >
        <DashboardCourseCardContent course={course} />
      </Box>
    );
  }

  return (
    <ClickableCardLink
      to={courseCardPath(course, linkToLastLesson)}
      ariaLabel={course.title}
      sx={courseCardShellSx}
    >
      <DashboardCourseCardContent course={course} />
    </ClickableCardLink>
  );
};

export default DashboardCourseCard;
